import { faSquareQuestion } from "@fortawesome/pro-regular-svg-icons"
import { faEmptySet, faImageLandscape, faImagePortrait, faVideo, faVideoSlash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Typography } from "@mui/material"
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getFormatedNumber } from "app/tools/number"
import { CURRENCIES } from "currency/model"
import { getValueInUserCurrency } from "currency/tools/common"
import { getEnvironmentNameFromId } from "environments/tools/common"
import { getCurrencySymbol } from "form/inventory/tools/common"
import { getWeatherIcon } from "form/tools/schedule"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React from "react"
import { connect } from "react-redux"
import { IReportResult, REPORTS_HIDDEN_COLUMN_KEYS, REPORTS_RESULT_LIST_DOM_ID } from "reports/model"
import { downloadReportResultCsvFile } from "reports/tools/csv"
import { getReportForm, sortReportColumns } from "reports/tools/ui"
import { getSspNameFromId } from "ssp/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { ListIdCell } from "ui/components/ListIdCell"
import { getDataGridTexts } from "ui/tools/list"
import { ListMessage } from "./ListMessage"

interface IReportsResultListProps {
  isCreation: boolean
}

interface IReportsResultListProps {
  isCreation: boolean
}

interface IReportsResultListConnectProps {
  results: IReportResult[] | null
  isLoading: boolean
  hasError: boolean
}

const renderReportCell = (
  key: keyof IReportResult,
  { row, rowNode }: GridRenderCellParams<any, IReportResult, any>
) => {
  switch (key) {
    case "campaign_name": {
      return (
        <ListIdCell
          id={row.campaign_id as number}
          label={row.campaign_name as string}
          cellId={`row-${rowNode.id}-${key}`}
        />
      )
    }
    case "ssp_id": {
      if (typeof row.ssp_id !== "number") {
        return ""
      }
      return (
        <ListIdCell
          id={row.ssp_id as number}
          label={getSspNameFromId(row.ssp_id as number)}
          cellId={`row-${rowNode.id}-${key}`}
        />
      )
    }
    case "screen_name": {
      if (typeof row.screen_name !== "string" || !row.screen_name) {
        return ""
      }
      return (
        <ListIdCell id={row.screen_id || "-"} label={row.screen_name as string} cellId={`row-${rowNode.id}-${key}`} />
      )
    }
    case "venue_type_id": {
      if (typeof row.venue_type_id !== "number") {
        return ""
      }
      return (
        <ListIdCell
          id={row.venue_type_id as number}
          label={getEnvironmentNameFromId(row.venue_type_id as number)}
          cellId={`row-${rowNode.id}-${key}`}
        />
      )
    }
    case "media_name": {
      if (typeof row.media_name !== "string" || !row.media_name) {
        return ""
      }
      return (
        <ListIdCell id={row.media_id as number} label={row.media_name as string} cellId={`row-${rowNode.id}-${key}`} />
      )
    }
    case "publisher_id": {
      if (!row.publisher_id) {
        return ""
      }
      return <ListIdCell id={"-" as any} label={row.publisher_id as string} cellId={`row-${rowNode.id}-${key}`} />
    }
    case "weather_id": {
      if (typeof row.weather_id !== "number" || row.weather_id === 0) {
        return ""
      }
      return <FontAwesomeIcon icon={getWeatherIcon(row.weather_id)} className="icon" />
    }
    case "order_name": {
      if (typeof row.order_name !== "string" || !row.order_name) {
        return ""
      }
      return (
        <ListIdCell id={row.order_id as number} label={row.order_name as string} cellId={`row-${rowNode.id}-${key}`} />
      )
    }
    case "media_is_portrait": {
      return (
        <FontAwesomeIcon icon={row.media_is_portrait ? faImagePortrait : faImageLandscape} className="icon media" />
      )
    }
    case "media_is_video": {
      return <FontAwesomeIcon icon={row.media_is_video ? faVideo : faVideoSlash} className="icon media" />
    }
    case "spend":
    case "ssp_spend":
    case "delivery_fees":
    case "ecpm": {
      return (
        <Typography color="textPrimary" fontSize=".8rem">
          {getFormatedNumber(
            Number(row[key] ? getValueInUserCurrency(CURRENCIES.CAD, row[key] as number).toFixed(3) : "0")
          )}{" "}
          {getCurrencySymbol()}
        </Typography>
      )
    }
    case "bids":
    case "buys":
    case "impressions":
    case "wins": {
      return getFormatedNumber(Number(row[key].toFixed(2)))
    }
    case "campaign_coordinate_lat":
    case "campaign_coordinate_lon":
    case "campaign_coordinate_radius": {
      const value = row[key] as number
      if (value === 0.000000000001) {
        return ""
      }
      return value
    }
    default: {
      return row[key]
    }
  }
}

const getColumnWidth = (key: string): number => {
  switch (key) {
    case "campaign_name":
    case "ssp_id":
    case "screen_name":
    case "venue_type_id":
    case "media_name":
    case "order_name":
    case "publisher_id": {
      return 200
    }
    default: {
      return 130
    }
  }
}

const ReportsResultListFn = React.memo(
  ({ results, isLoading, hasError }: IReportsResultListProps & IReportsResultListConnectProps) => {
    if (hasError) {
      return <UnexpectedErrorPage />
    }
    if (isLoading) {
      return <LinearLoader id="reports-result-loading" sx={{ m: "2rem" }} />
    }
    if (results === null) {
      return <ListMessage message={translate("reports.form.help")} icon={faSquareQuestion} />
    }
    if (results.length === 0) {
      return <ListMessage id="reports-result-empty" message={translate("label.no.match")} icon={faEmptySet} small />
    }
    const firstResult = results[0]
    const keys: Array<keyof IReportResult> = Object.keys(firstResult) as Array<keyof IReportResult>
    const columns: Array<GridColDef<IReportResult>> = []
    for (const key of keys) {
      if (REPORTS_HIDDEN_COLUMN_KEYS.includes(key)) {
        continue
      }
      columns.push({
        field: key,
        headerName: translate(`reports.column.${key}`),
        renderCell: params => renderReportCell(key, params),
        width: getColumnWidth(key)
      })
    }
    columns.sort(sortReportColumns)
    return (
      <FlexBox
        id={REPORTS_RESULT_LIST_DOM_ID}
        vertical
        gap
        ai="start"
        fullWidth
        sx={{
          "p": "1rem",
          "height": "100%",
          "minHeight": "100%",
          "maxHeight": "100%",
          "& .icon": {
            color: theme => theme.palette.text.primary,
            fontSize: "1.2rem"
          }
        }}>
        <FlexBox jc="space-between" fullWidth sx={{ pr: "2rem" }} id="reports-result-container">
          <Typography color="textPrimary" fontSize="1.1rem" id="reports-result-title">
            {translate("reports.result.title", results.length)}
          </Typography>
          <Button id="reports-result-export" variant="outlined" onClick={() => downloadReportResultCsvFile(results)}>
            {translate("label.export.csv")}
          </Button>
        </FlexBox>
        <DataGrid
          rows={results}
          columns={columns}
          autoPageSize
          disableSelectionOnClick
          localeText={getDataGridTexts()}
          sx={{ width: "100%", height: "22rem" }}
          pagination
        />
      </FlexBox>
    )
  }
)

export const ReportsResultList = connect(
  (state: IRootState, ownProps: IReportsResultListProps): IReportsResultListConnectProps => {
    const form = getReportForm(ownProps.isCreation, state.reports)
    return {
      results: form.results,
      isLoading: form.isResultLoading,
      hasError: form.hasResultLoadingError
    }
  }
)(ReportsResultListFn)
